<template>
    <v-container fluid>
      <v-card class="ma-2">
        <v-card-text>
          <v-overlay absolute v-if="loading">
            <v-progress-circular
              indeterminate
              :size="50"
              :width="5"
              color="primary"
            >
            </v-progress-circular>
          </v-overlay>

          <v-row class="py-0">
            <v-col cols="3" class="mt-1">
               <v-card-title>{{ title }}</v-card-title>
            </v-col>
            <v-col class="pt-1">
              <v-text-field
                v-model="filter.search"
                :label="$t('Search') + '...'"
                prepend-icon="mdi-magnify"
                class="ml-2"
                v-debounce:300ms="changeFilter"
              ></v-text-field>
            </v-col>
            <v-col class="pt-1">
              <DatePickerISOCompliant
                :label="$t('From')"
                clearable
                class="ml-2"
                v-model="filter.from"
                @change="changeFilter"
              />
            </v-col>
            <v-col class="pt-1">
              <DatePickerISOCompliant
                :label="$t('To')"
                clearable
                class="ml-2"
                v-model="filter.to"
                @change="changeFilter"
              />
            </v-col>
            <v-col class="d-flex text-xs-right pt-1 justify-space-around">
              <v-btn
                small
                color="success"
                @click.prevent="onRestoreSelected"
                :loading="restoreLoading"
                class="mt-4 ma-0"
              >
                <v-icon left>mdi-history</v-icon>
                {{ $t("Restore") }}
              </v-btn>
              <v-btn
                small
                color="red"
                @click.prevent="onDeleteSelected"
                :loading="deleteLoading"
                class="mt-4 ma-0 ms-2"
              >
                <v-icon left>mdi-delete</v-icon>
                {{ $t("Site.DocumentManagement.Destroy") }}
              </v-btn>
            </v-col>
          </v-row>
          <simple-table
              :ref="tableKey"
              :tableConfig="tableConfig"
              :fetchData="getData"
              :tableData="tableData"
            ></simple-table>
        </v-card-text>
      </v-card>
      <v-dialog v-model="isModalOpen" max-width="450px">
        <v-card>
          <v-card-title>{{ this.$t("Detail") }}</v-card-title>
          <simple-table
              :tableConfig="modalTableConfig"
              :tableData="modalData"
          ></simple-table>
          <v-card-actions class="d-flex justify-center">
            <v-btn class="ml-4" color="primary" @click="closeModal">{{ this.$t("Close") }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </template>

<script>
import DocumentService from '@/services/DocumentService';
import SearchType from '@/ecm/variables/SearchType';
import Utils from '@/ecm/common/Utils';
import SimpleTable from '@/components/base/SimpleTable.vue';
import DatePickerISOCompliant from '@/components/base/DatePickerISOCompliant.vue';
export default {
    components: {
        SimpleTable,
        DatePickerISOCompliant
    },
    data() {
        return {
            deleteLoading: false,
            restoreLoading: false,
            loading: false,
            title: this.$t('DeletedDocuments'),
            dataSource: [],
            tableKey: 'deletedDocumentList',
            isModalOpen: false,
            modalData: [],
            filter: {
                from: null,
                to: null,
                search: null,
            },
            tableData: [],
            modalTableHeaders: [],
        };
    },
    computed: {
        modalTableConfig() {
            if (this.modalData.length == 0) return {};
            return {
                dense: true,
                allowSelection: false,
                showActions: false,
                showPagination: false,
                allowSorting: false,
                headers: this.modalTableHeaders
            };
        },
        tableConfig() {
            const tableHeaders = [];
            tableHeaders.push({ title: 'ContentType', titleTranslation: 'ContentType', allowSorting: true });
            tableHeaders.push({ title: 'UserName', titleTranslation: 'User', allowSorting: true });
            tableHeaders.push({ title: 'DelDate', titleTranslation: 'Date', allowSorting: true });
            tableHeaders.push({ title: 'VersionNumber', titleTranslation: 'Version', allowSorting: true });
            tableHeaders.push({ title: 'IpAddress', titleTranslation: 'IP', allowSorting: true });

            return {
                dense: false,
                allowSelection: true,
                selectAllTitleTranslation: 'Documents',
                showActions: true,
                showPagination: true,
                allowSorting: true,
                headers: tableHeaders,
                actionsConfig: {
                    details: { icon: 'mdi-information', color: 'grey', translation: 'Detail' },
                    view: { icon: 'mdi-eye', color: 'blue', translation: 'View' },
                    restore: { icon: 'mdi-history', color: 'success', translation: 'Restore' },
                    delete: { icon: 'mdi-delete', color: 'red', translation: 'Site.DocumentManagement.Destroy' }
                }
            };
        },
    },
    methods: {
        openModal(id) {
            this.loading = true;
            DocumentService.get(id)
                .then((data) => {
                    let fieldsToShow = 3;
                    const fields = [];
                    let count = 0;
                    this.modalData = [];
                    this.modalTableHeaders = [];
                    if (data.Fields.length < 3) {
                        fieldsToShow = data.Fields.length;
                    }
                    for (let i = 0; i < data.Fields.length && count < fieldsToShow; i++) {
                        if (data.Fields[i].Value !== '') {
                            const field = {
                                title: data.Fields[i].Value,
                            };
                            this.modalTableHeaders.push({ title: data.Fields[i].Title });
                            fields.push(field);
                            count++;
                        }
                    }
                    const row = {
                        fields: fields,
                        actions: [],
                        isSelected: false,
                        id: id
                    };
                    this.modalData.push(row);
                    this.loading = false;
                    this.isModalOpen = true;
                })
                .catch(() => {
                    this.loading = false;
                    this.closeModal();
                });
        },
        closeModal() {
            this.isModalOpen = false; // Cierra el modal
            this.modalData.fields = [];
        },
        getRoute(id) {
            const pattern = {
                searchPattern: id,
                searchAllVersions: true,
                allowDeleted: true,
            };
            let route = '/search/';
            route += `${SearchType.DocumentIDs}/${encodeURIComponent(JSON.stringify(pattern))}`;
            return route;
        },
        async getData(pagination) {
            this.loading = true;

            pagination.filter = this.filter.search;
            pagination.dateFrom = this.filter.from;
            pagination.dateTo = this.filter.to;

            const data = await DocumentService.getDeletedGrid(pagination);
            this.loading = false;
            if (!data) {
                return;
            }
            this.dataSource = data.Result;
            this.tableData = this.transformTableData(data.Result);
            return {
                totalItems: data.Count,
                totalPages: Math.ceil(data.Count / pagination.pageSize),
                result: data.Result
            };
        },
        transformTableData(rows) {
            return rows.map(row => {
                const tableFields = [
                    { title: row.ContentType },
                    { title: row.UserName },
                    { title: row.DelDate, function: () => this.calculateCellValue(row.DelDate) },
                    { title: row.VersionNumber },
                    { title: row.IpAddress },
                ];

                const tableFieldActions = [
                    { icon: 'mdi-information', color: 'grey', function: () => this.openModal(row.Id), translation: 'Detail', redirect: false },
                    { icon: 'mdi-eye', color: 'blue', function: this.getRoute(row.Id), translation: 'View', redirect: true },
                    { icon: 'mdi-history', color: 'success', function: () => this.restoreItem(row.Id), translation: 'Restore', redirect: false },
                    { icon: 'mdi-delete', color: 'red', function: () => this.deleteItem(row.Id), translation: 'Site.DocumentManagement.Destroy', redirect: false },
                ];

                return {
                    id: row.Id,
                    fields: tableFields,
                    actions: tableFieldActions,
                    isSelected: false
                };
            });
        },
        deleteItem(id) {
            this.$confirm(this.$t('Site.DocumentManagement.DestroyDocumentsConfirmation'), {
                title: this.$t('Site.DocumentManagement.Destroy'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Site.DocumentManagement.Destroy'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    DocumentService.removeDeleted([id]).then((result) => {
                        this.$refs[this.tableKey].refreshPage();
                    });
                }
            });
        },
        restoreItem(id) {
            this.$confirm(this.$t('DocumentsRestoreConfirmation'), {
                title: this.$t('Restore'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Restore'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    DocumentService.restoreDeleted([id]).then((result) => {
                        this.validateListOfRestoreObjects(result);
                        this.$refs[this.tableKey].refreshPage();
                    });
                }
            });
        },
        validateListOfRestoreObjects(ListsOfObjectsRestore) {
            let messageListObjectsRestore;
            if (ListsOfObjectsRestore.ListInvalidObjectsRestore.length !== 0) {
                messageListObjectsRestore = this.createMessageForListofInvalidRestoreItems(ListsOfObjectsRestore.ListInvalidObjectsRestore);
                Utils.toast.error(this.$t('InvalidRestoreUniqueFields') + messageListObjectsRestore);

            }
            if (ListsOfObjectsRestore.ListValidObjectsRestore.length !== 0) {
                messageListObjectsRestore = ListsOfObjectsRestore.ListValidObjectsRestore.join(',');
                Utils.toast.success(this.$t('ValidRestoreObjects') + messageListObjectsRestore);
            }
            if (ListsOfObjectsRestore.ListValidObjectsRestore.length != 0 && ListsOfObjectsRestore.ListInvalidObjectsRestore.length != 0) {
                const messageListObjectInvalid = this.createMessageForListofInvalidRestoreItems(ListsOfObjectsRestore.ListInvalidObjectsRestore);
                messageListObjectsRestore = ListsOfObjectsRestore.ListValidObjectsRestore.join(',');
                Utils.toast.warning(this.$t('ValidRestoreObjects') + messageListObjectsRestore + '\n' + this.$t('InvalidRestoreUniqueFields') + messageListObjectInvalid);
            }

        },
        createMessageForListofInvalidRestoreItems(ListofElements) {
            const message = ListofElements.reduce((acc, obj) => {
                acc[obj.ExistingUniqueObjectIds] = acc[obj.ExistingUniqueObjectIds] || [];
                acc[obj.ExistingUniqueObjectIds].push(obj.Code);
                return acc;
            }, {});

            const finalMessage = Object.entries(message).map(([objectId, codes]) => {
                return `${objectId}(${codes.join(',')})`;
            }).join(', ');
            return finalMessage;
        },
        onRestoreSelected() {
            const selectedItems = this.tableData
                .filter((x) => x.isSelected)
                .map((t) => t.id);

            if (selectedItems.length === 0) {
                Utils.toast.warning(this.$t('ItemsSelectionError'));
                return;
            }

            this.$confirm(this.$t('DocumentsRestoreConfirmation'), {
                title: this.$t('Restore'),
                icon: 'mdi-alert',
                color: 'warning',
                buttonTrueText: this.$t('Restore'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    this.restoreLoading = true;
                    DocumentService.restoreDeleted(selectedItems)
                        .then((result) => {
                            this.validateListOfRestoreObjects(result);
                            this.$refs[this.tableKey].refreshPage();
                        })
                        .catch(() => {
                            //error
                        })
                        .then(() => {
                            this.restoreLoading = false;
                        });
                }
            });
        },
        onDeleteSelected() {
            const selectedItems = this.tableData
                .filter((x) => x.isSelected)
                .map((t) => t.id);
            if (selectedItems.length === 0) {
                Utils.toast.warning(this.$t('ItemsSelectionError'));
                return;
            }

            this.$confirm(this.$t('Site.DocumentManagement.DestroyDocumentsConfirmation'), {
                title: this.$t('Site.DocumentManagement.Destroy'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Site.DocumentManagement.Destroy'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res === 1) {
                    this.deleteLoading = true;
                    DocumentService.removeDeleted(selectedItems)
                        .then(() => {
                            this.$refs[this.tableKey].refreshPage();
                        })
                        .catch(() => {
                            //error
                        })
                        .then(() => {
                            this.deleteLoading = false;
                        });
                }
            });
        },
        calculateCellValue(delDate) {
            return Utils.date.toUserFormatFromIso(delDate);
        },
        changeFilter() {
            this.$refs[this.tableKey].resetPagination();
        },
    },
};
</script>
